import { useState, useEffect } from "react";
import api from "../../axiosconfig";
import { monthKeyToRange, monthKeyToLabel } from "../../utils/dateUtils";

const defaultScores = {
    physical: 0,
    emotional: 0,
    acceptance: 0,
    relationships: 0,
    lovingKindness: 0,
    intelligence: 0,
};

export default function usePearliRange(viewMode, startKey, endKey, buildUrl) {
    const [prevScores, setPrevScores] = useState(defaultScores);
    const [focusScores, setFocusScores] = useState(defaultScores);
    const [prevCount, setPrevCount] = useState(0);
    const [focusCount, setFocusCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // Auto label for UI
    const prevLabel = monthKeyToLabel(startKey);
    const focusLabel = monthKeyToLabel(endKey);

    useEffect(() => {
        if (viewMode !== "range") return;

        const fetchRangeScores = async () => {
            try {
                setLoading(true);

                const prev = monthKeyToRange(startKey);
                const focus = monthKeyToRange(endKey);

                const [pRes, fRes] = await Promise.all([
                    api.get(buildUrl(prev.start, prev.end)),
                    api.get(buildUrl(focus.start, focus.end)),
                ]);

                const p = pRes?.data?.data;
                const f = fRes?.data?.data;

                setPrevScores({
                    physical: p?.scores?.AvgP ?? null,
                    emotional: p?.scores?.AvgE ?? null,
                    acceptance: p?.scores?.AvgA ?? null,
                    relationships: p?.scores?.AvgR ?? null,
                    lovingKindness: p?.scores?.AvgL ?? null,
                    intelligence: p?.scores?.AvgI ?? null,
                });

                setFocusScores({
                    physical: f?.scores?.AvgP ?? null,
                    emotional: f?.scores?.AvgE ?? null,
                    acceptance: f?.scores?.AvgA ?? null,
                    relationships: f?.scores?.AvgR ?? null,
                    lovingKindness: f?.scores?.AvgL ?? null,
                    intelligence: f?.scores?.AvgI ?? null,
                });

                setPrevCount(p?.count ?? 0);
                setFocusCount(f?.count ?? 0);

            } catch (e) {
                console.error("usePearliRange error:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchRangeScores();
    }, [viewMode, startKey, endKey, buildUrl]);

    // Determine if we have valid data for each period
    const hasPrevData = 
        prevCount > 0 && 
        Object.values(prevScores).every(v => v !== null && typeof v === 'number');
    
    const hasFocusData = 
        focusCount > 0 && 
        Object.values(focusScores).every(v => v !== null && typeof v === 'number');

    return {
        prevScores,
        focusScores,
        prevCount,
        focusCount,
        prevLabel,
        focusLabel,
        loading,
        hasPrevData,
        hasFocusData,
    };
}
